import { useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import type {
  PropertySubscription,
  SubscriptionSummary,
} from "../PropertySubscriptionContext";
import { isJwtValid } from "../AuthContext";
import { apiClient } from "@/api/client";
import { logger } from "@/utils/debugLogger";
import { storyQueryKeys } from "@/queries/storyQueryKeys";

export function getAxiosStatus(error: unknown): number | null {
  const status = (error as AxiosError | undefined)?.response?.status;
  return typeof status === "number" ? status : null;
}

interface SubscriptionQueryData {
  propertySubscriptions: PropertySubscription[];
  summary: SubscriptionSummary | null;
}

interface UseSubscriptionsQueryParams {
  isAuthenticated: boolean;
  accessToken: string | null;
  sessionKey: string;
  getStoredAccessToken: () => string | null;
}

const MAX_EXPIRY_TIMER_MS = 2_147_000_000;

function nearestActivePackageExpiry(
  subscriptions: PropertySubscription[],
): number | null {
  const expiries = subscriptions.flatMap((subscription) => {
    if (subscription.package_state !== "active" || !subscription.expires_at) {
      return [];
    }
    const expiresAt = Date.parse(subscription.expires_at);
    return Number.isFinite(expiresAt) ? [expiresAt] : [];
  });
  return expiries.length ? Math.min(...expiries) : null;
}

export interface SubscriptionsQueryResult {
  propertySubscriptions: PropertySubscription[];
  summary: SubscriptionSummary | null;
  isLoading: boolean;
  hasAttemptedLoad: boolean;
  error: string | null;
  refreshSubscriptions: () => Promise<void>;
}

export function useSubscriptionsQuery({
  isAuthenticated,
  accessToken,
  sessionKey,
  getStoredAccessToken,
}: UseSubscriptionsQueryParams): SubscriptionsQueryResult {
  // Scope the cache to the authenticated owner identity. Credentials never enter
  // the key, so token rotation keeps the same owner cache while account changes
  // cannot serve another owner's subscription snapshot.
  // React Query's cache namespace is determined solely by the queryKey.
  // The `enabled` flag only controls whether a refetch is triggered; it does NOT
  // prevent serving stale cached data to a different user when the queryKey is the same.
  // By including the stable owner namespace, we ensure:
  //   1. Each user's subscriptions are cached separately
  //   2. N concurrent consumers on the same user still dedupe to one network request
  //      (stable owner = stable queryKey = single request across all observers)
  //   3. Cross-user leaks are prevented by cache isolation at the queryKey boundary
  const subscriptionsQuery = useQuery<SubscriptionQueryData, Error>({
    queryKey: storyQueryKeys.property.subscriptions(sessionKey),
    // Defense-in-depth against the cold-load 401: even if an expired access token
    // were transiently present in state, isJwtValid gates the probe so it never
    // fires with a token the server will reject. The authBootstrap fix ensures an
    // expired token is never published in the first place; this is the second line.
    enabled: isAuthenticated && accessToken !== null && isJwtValid(accessToken),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      const status = getAxiosStatus(error);
      if (status && status < 500) {
        return false;
      }
      return failureCount < 2;
    },
    queryFn: async () => {
      const response = await apiClient.get(
        "/api/users/property-subscriptions/",
        {
          skipAuth401: true,
          skipErrorLogging: true,
        },
      );
      const data = response.data;

      logger.info(
        {
          component: "PropertySubscriptionContext",
          action: "loadSubscriptions",
        },
        `Loaded ${data.property_subscriptions?.length || 0} subscriptions`,
        data,
      );

      const subs: PropertySubscription[] = (
        data.property_subscriptions || []
      ).map((sub: PropertySubscription) => ({
        ...sub,
        tier: (sub.tier?.toLowerCase() ??
          "free") as PropertySubscription["tier"],
      }));
      return {
        propertySubscriptions: subs,
        summary: data.summary || null,
      };
    },
  });

  useEffect(() => {
    if (!subscriptionsQuery.error) {
      return;
    }
    const status = getAxiosStatus(subscriptionsQuery.error);
    logger.info(
      {
        component: "PropertySubscriptionContext",
        action: "loadSubscriptions",
      },
      `Property subscriptions unavailable${
        status !== null ? ` (status ${status})` : ""
      }`,
      subscriptionsQuery.error,
    );
  }, [subscriptionsQuery.error]);

  // React Query recreates the whole query result object on every render, but the
  // observer's `refetch` method is bound once at construction and is referentially
  // stable across renders. Depending on `refetch` (not the whole query object) keeps
  // `refreshSubscriptions` stable, so it does not churn its identity every render.
  const { refetch: refetchSubscriptions } = subscriptionsQuery;
  const nearestExpiry = nearestActivePackageExpiry(
    subscriptionsQuery.data?.propertySubscriptions ?? [],
  );
  useEffect(() => {
    if (nearestExpiry === null) return;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const scheduleExpiryRefresh = (): void => {
      const remaining = nearestExpiry - Date.now();
      if (remaining <= 0) {
        void refetchSubscriptions();
        return;
      }
      timeoutId = setTimeout(
        scheduleExpiryRefresh,
        Math.min(remaining + 25, MAX_EXPIRY_TIMER_MS),
      );
    };
    scheduleExpiryRefresh();
    return () => {
      if (timeoutId !== null) clearTimeout(timeoutId);
    };
  }, [nearestExpiry, refetchSubscriptions]);
  const refreshSubscriptions = useCallback(async () => {
    if (!getStoredAccessToken()) {
      return;
    }
    await refetchSubscriptions();
  }, [getStoredAccessToken, refetchSubscriptions]);

  // Memoize propertySubscriptions to ensure stable reference across renders
  // Prevents dependency array churn when passing to consumers
  const propertySubscriptions = useMemo(
    () => subscriptionsQuery.data?.propertySubscriptions ?? [],
    [subscriptionsQuery.data?.propertySubscriptions],
  );

  const summary = useMemo(
    () => subscriptionsQuery.data?.summary ?? null,
    [subscriptionsQuery.data?.summary],
  );

  const isLoading = subscriptionsQuery.isFetching;
  const hasAttemptedLoad =
    isAuthenticated && Boolean(accessToken)
      ? subscriptionsQuery.isFetched || subscriptionsQuery.isError
      : false;
  const error = subscriptionsQuery.error?.message ?? null;

  return {
    propertySubscriptions,
    summary,
    isLoading,
    hasAttemptedLoad,
    error,
    refreshSubscriptions,
  };
}
